import * as z from 'zod';

import { PAY_STATION_UI_MODAL, PayStationPaymentType, PIPELINE_PROFILE } from 'src/features/payment/utils/types';
import { LOCALE } from 'src/providers/types';
import { CURRENCY } from 'src/types';
import { validationMessagesMap } from 'src/utils/messages';

export type PayStationFormSchemaValues = z.infer<typeof payStationFormSchema>;

const nominalValueSchema = z.object({
    currency: z.enum(CURRENCY),
    value: z.string(),
    enable: z.boolean(),
});
export const payStationFormSchema = z
    .object({
        name: z.string().nonempty(),
        code: z.string().nonempty(),
        site: z.string(),
        settings: z.object({
            enabled_payment_methods: z.array(z.enum(PayStationPaymentType)).nonempty(),
            password: z.string().nullish(),
            languages: z.array(z.enum(LOCALE)).nullish(),
            currencies: z.array(z.enum(CURRENCY)).nonempty(),
            lost_ticket: z.boolean().nullish(),
            web_id: z.string().nullish(),
            home_qr_value: z.string().nullish(),
            support_phone: z.string().nullish(),
            enable_free_stay: z.boolean().nullish(),
            default_language: z.enum(LOCALE).nullish(),
            enable_subscriptions: z.boolean().nullish(),
            enable_settings_enter: z.boolean().nullish(),
            home_pay_button_text: z
                .object({
                    bg: z.string().nullish(),
                    en: z.string().nullish(),
                })
                .nullish(),
            coin_selector: z.object({
                accepted_nominals: z.array(nominalValueSchema),
                payout_nominals: z.array(nominalValueSchema),
            }),
            banknote_validator: z.object({
                accepted_nominals: z.array(nominalValueSchema),
                payout_nominals: z.array(nominalValueSchema),
            }),
            pipeline_profile: z.enum(PIPELINE_PROFILE),
        }),
        gateway: z.string().optional().nullable(),
        ui_mode: z.enum(PAY_STATION_UI_MODAL),
        backend_url: z.url({ message: validationMessagesMap.validationErrorURL }),
        backend_api_key: z.string().nonempty(),
    })
    .superRefine((data, ctx) => {
        if (
            (data.ui_mode === PAY_STATION_UI_MODAL.UNATTENDED ||
                data.ui_mode === PAY_STATION_UI_MODAL.READ_ONLY_DISPLAY) &&
            !data.gateway
        ) {
            ctx.addIssue({
                path: ['gateway'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
        if (data.ui_mode === PAY_STATION_UI_MODAL.INTERACTIVE) {
            if (!data.settings.password) {
                ctx.addIssue({
                    path: ['settings.password'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (!data.settings.languages) {
                ctx.addIssue({
                    path: ['settings.languages'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (data.settings.lost_ticket == null) {
                ctx.addIssue({
                    path: ['settings.lost_ticket'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (data.settings.enable_free_stay == null) {
                ctx.addIssue({
                    path: ['settings.enable_free_stay'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (!data.settings.default_language) {
                ctx.addIssue({
                    path: ['settings.default_language'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (data.settings.enable_subscriptions == null) {
                ctx.addIssue({
                    path: ['settings.enable_subscriptions'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (data.settings.enable_settings_enter == null) {
                ctx.addIssue({
                    path: ['settings.enable_settings_enter'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
        }
    });
